import { z } from 'zod';
import { householdDtoSchema, householdRoleSchema, unitSystemSchema, userDtoSchema, } from './auth.js';
import { equipmentKindSchema, idSchema } from './common.js';
import { allergenSchema, dietSchema } from './profiles.js';
/** Household management payloads (Phase 1). Owned by the households/admin work. */
export const createHouseholdRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    timezone: z.string().max(64).optional(),
    unitSystem: unitSystemSchema.optional(),
    defaultServings: z.number().int().min(1).max(50).optional(),
});
export const updateHouseholdRequestSchema = createHouseholdRequestSchema.partial().extend({
    /** Thaw reminder lead time (D28), 1 hour to 1 week. */
    thawLeadHours: z.number().int().min(1).max(168).optional(),
});
export const memberDtoSchema = z.object({
    user: userDtoSchema.pick({ id: true, email: true, displayName: true }),
    role: householdRoleSchema,
    joinedAt: z.iso.datetime(),
});
export const membersResponseSchema = z.object({ members: z.array(memberDtoSchema) });
export const updateMemberRoleRequestSchema = z.object({ role: householdRoleSchema });
// ------------------------------------------------- household people (D30) ---
/**
 * Everyone in the family who can be rated for: each account member plus
 * people without an account (e.g. young children). Linked people carry the
 * member's current display name.
 */
export const householdPersonDtoSchema = z.object({
    id: idSchema,
    name: z.string(),
    /** The member's user id; null for a person without an account. */
    userId: idSchema.nullable(),
    hasAccount: z.boolean(),
    /** True for the signed-in caller's own person. */
    isMe: z.boolean(),
    /** Eating profile (D31). Defaults keep older cached payloads parseable. */
    allergens: z.array(allergenSchema).default([]),
    diets: z.array(dietSchema).default([]),
    notes: z.string().nullable().default(null),
    /** Counts of this person's likes/dislikes (stated + learned). */
    preferences: z
        .object({
        likes: z.number().int().nonnegative(),
        dislikes: z.number().int().nonnegative(),
        learned: z.number().int().nonnegative(),
    })
        .default({ likes: 0, dislikes: 0, learned: 0 }),
});
/** GET /api/v1/households/me/people */
export const householdPeopleResponseSchema = z.object({
    people: z.array(householdPersonDtoSchema),
});
/** POST /api/v1/households/me/people and PATCH …/people/:id (people without an account). */
export const householdPersonRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
export const inviteDtoSchema = z.object({
    id: idSchema,
    code: z.string(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    usedAt: z.iso.datetime().nullable(),
});
export const createInviteRequestSchema = z.object({
    /** Hours until expiry, default 72. */
    expiresInHours: z
        .number()
        .int()
        .min(1)
        .max(24 * 30)
        .optional(),
});
export const invitesResponseSchema = z.object({ invites: z.array(inviteDtoSchema) });
export const acceptInviteRequestSchema = z.object({ code: z.string().trim().min(4).max(64) });
export const switchHouseholdRequestSchema = z.object({ householdId: idSchema });
export const equipmentDtoSchema = z.object({
    id: idSchema,
    kind: equipmentKindSchema,
    count: z.number().int().positive(),
    label: z.string().nullable(),
});
export const equipmentResponseSchema = z.object({ equipment: z.array(equipmentDtoSchema) });
export const upsertEquipmentRequestSchema = z.object({
    kind: equipmentKindSchema,
    count: z.number().int().min(0).max(20),
    label: z.string().trim().max(80).nullable().optional(),
});
/** GET /api/v1/households/me, POST /join, POST /switch */
export const householdMeResponseSchema = z.object({
    household: householdDtoSchema,
    role: householdRoleSchema,
    /** Stored photo bytes attributed to the household (GET /me only). */
    mediaBytesUsed: z.number().int().nonnegative().optional(),
    /** Per-household photo storage quota in bytes (GET /me only). */
    mediaQuotaBytes: z.number().int().nonnegative().optional(),
});
/** PUT /api/v1/households/me/equipment — replaces the whole list; count 0 removes. */
export const replaceEquipmentRequestSchema = z.object({
    equipment: z.array(upsertEquipmentRequestSchema).max(50),
});
export const householdSummarySchema = householdDtoSchema.extend({
    memberCount: z.number().int(),
    disabledReason: z.string().nullable(),
});
//# sourceMappingURL=households.js.map